/**
 * The build-time environment, read in ONE place (spec §13).
 *
 * §13: "The API base URL and all hostnames live in config, not code. No
 * hostname string literals in source files."
 *
 * That rule is why this module exists and why nothing else in `src/` may touch
 * `import.meta.env`. A grep for `shubham.engineering` or `localhost` across
 * `apps/app/src` must return nothing — if it ever returns a hit, a build has
 * acquired a hardcoded target and the Capacitor shell (§1.2) or the production
 * deploy will silently talk to the wrong server.
 *
 * There is deliberately NO DEFAULT VALUE. A missing `VITE_API_BASE_URL` throws
 * at module load with an actionable message, because the alternative — falling
 * back to `http://localhost:3000` — produces a production bundle that looks
 * fine, deploys fine, and then fails on the owner's phone with a network error
 * nobody can explain.
 */

function required(name: keyof ImportMetaEnv): string {
  const value = import.meta.env[name];
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(
      `Missing ${name}. Copy apps/app/.env.example to apps/app/.env.local and set it ` +
        `(dev: http://localhost:3000/api/v1). Vite inlines VITE_* at BUILD time, ` +
        `so a change needs a rebuild, not a restart.`,
    );
  }
  return value.trim();
}

/**
 * The API root, without a trailing slash — e.g. `/api/v1` or
 * `https://host/api/v1`. `src/lib/api.ts` joins paths onto it.
 *
 * The trailing slash is stripped here rather than at each call site so that
 * `${API_BASE_URL}/auth/refresh` cannot produce a double slash. Some proxies
 * treat `//auth` as a different path, and it would fail only in production.
 */
export const API_BASE_URL: string = required("VITE_API_BASE_URL").replace(/\/+$/, "");

/** True in `vite dev` / `vitest`. Never used to choose a hostname. */
export const IS_DEV: boolean = import.meta.env.DEV;
